// src/components/fees/FeeType.js
import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Grid, IconButton, Paper, Skeleton, Snackbar, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TextField,
  Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { feeAPI } from '../../services/api';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const INITIAL = { name: '', description: '' };

const FeeTypes = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.FEES.TYPES, ACTIONS.READ);
  const canCreate = can(RESOURCES.FEES.TYPES, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.FEES.TYPES, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.FEES.TYPES, ACTIONS.DELETE);
  const [feeTypes, setFeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [formData, setFormData] = useState(INITIAL);
  const [page, setPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (canRead) fetchFeeTypes();
  }, [page, pageSize, canRead]);

  const fetchFeeTypes = async () => {
    setLoading(true);
    try {
      const response = await feeAPI.getTypes({ page: page + 1, per_page: pageSize });
      const data = response.data;
      setFeeTypes(Array.isArray(data) ? data : data.data || []);
      setTotalRecords(data.total || (Array.isArray(data) ? data.length : 0));
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch fee types', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({ name: type.name, description: type.description || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await feeAPI.updateType(editingType.id, formData);
        setSnackbar({ open: true, message: 'Fee type updated successfully', severity: 'success' });
      } else {
        await feeAPI.createType(formData);
        setSnackbar({ open: true, message: 'Fee type created successfully', severity: 'success' });
      }
      fetchFeeTypes();
      resetForm();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    }
  };

  const confirmDeleteAction = async () => {
    try {
      await feeAPI.deleteType(confirmDelete);
      setSnackbar({ open: true, message: 'Fee type deleted successfully', severity: 'success' });
      fetchFeeTypes();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete fee type', severity: 'error' });
    } finally {
      setConfirmDelete(null);
    }
  };

  const resetForm = () => { setFormData(INITIAL); setEditingType(null); setShowForm(false); };

  if (loading) return <Skeleton variant="rectangular" width="100%" height={200} />;
  if (!canRead) return <Alert severity="error">You do not have permission to view fee types.</Alert>;

  return (
    <Box>
      <Box mb={4}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>Fee Types</Typography>
            <Typography variant="body1" color="text.secondary">
              Create reusable fee types (e.g., Tuition, Lab Fee, Sports Fee)
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {canCreate && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowForm(true)}>
                  Add Fee Type
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={showForm} onClose={resetForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingType ? 'Edit Fee Type' : 'Add New Fee Type'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <TextField
              fullWidth label="Name" value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Tuition Fee" required margin="normal"
            />
            <TextField
              fullWidth label="Description" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Annual tuition charges" multiline rows={3} margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingType ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Fee Type</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this fee type? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Fee Types ({totalRecords})</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feeTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No fee types created yet</TableCell>
                  </TableRow>
                ) : (
                  feeTypes.map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell><strong>{type.name}</strong></TableCell>
                      <TableCell>{type.description || '—'}</TableCell>
                      <TableCell>{new Date(type.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><Chip label="Active" size="small" color="success" /></TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {canUpdate && (
                            <IconButton size="small" color="primary" onClick={() => handleEdit(type)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canDelete && (
                            <IconButton size="small" color="error" onClick={() => setConfirmDelete(type.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 15, 20, 50]}
            labelRowsPerPage="Rows per page"
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeTypes;
